import { FC, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import jsQR from 'jsqr';

import { IdParamsType } from 'utils/types';
import { MainLayout } from 'layouts/MainLayout/MainLayout';
import { validateEncryptedTicket } from './services';
import { ScanResultComponent } from './ScanResultComponent';
import { CameraScanner } from './CameraScanner';
import { ManualCheckIn } from './ManualCheckIn';
import { haptic } from './haptics';

import * as Styled from './Scanner.styles';
import { airbrake } from 'ErrorBoundary';

export interface QrPayload {
	user_id: number;
	code: string;
	ticketing_mode: string;
}

/**
 * Parses a raw QR string into the expected ticket payload.
 * Returns null when the string is not valid JSON or does not carry the
 * required fields, so every call site gets a single, consistent failure
 * path instead of an uncaught exception.
 */
export function parseQrPayload(raw: string): QrPayload | null {
	try {
		const parsed = JSON.parse(raw);
		if (
			typeof parsed?.user_id !== 'number' ||
			typeof parsed?.code !== 'string' ||
			typeof parsed?.ticketing_mode !== 'string'
		) {
			return null;
		}
		return parsed as QrPayload;
	} catch {
		return null;
	}
}

type ScannerMode = 'camera' | 'manual';

export const Scanner: FC = () => {
	const [mode, setMode] = useState<ScannerMode>('camera');
	const [qrData, setQrData] = useState<QrPayload>();
	const [isActive, setIsActive] = useState(true);
	const [cameraError, setCameraError] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [scanResult, setScanResult] = useState<{
		type: 'success' | 'error';
		data?: any;
		errors?: any[];
	} | null>(null);

	const fileInputRef = useRef<HTMLInputElement>(null);
	const cameraFailureReportedRef = useRef(false);
	const { id: eventId } = useParams<IdParamsType>();

	const getErrorMessage = (error: unknown): string => {
		if (!error) return 'Unknown QR reader error';

		if (typeof error === 'string') return error;

		if (typeof error === 'object') {
			const maybeMessage = (error as { message?: unknown }).message;
			if (typeof maybeMessage === 'string' && maybeMessage.trim().length > 0) {
				return maybeMessage;
			}

			const maybeName = (error as { name?: unknown }).name;
			if (typeof maybeName === 'string' && maybeName.trim().length > 0) {
				return maybeName;
			}
		}

		return 'Unknown QR reader error';
	};

	const { mutate: sendCode } = useMutation(validateEncryptedTicket, {
		onSuccess: ({ data }) => {
			const hasErrors = data.errors && data.errors.length > 0;
			haptic(hasErrors ? 'error' : 'success');

			if (hasErrors) {
				setScanResult({
					type: 'error',
					data: data,
					errors: data.errors,
				});
			} else {
				setScanResult({
					type: 'success',
					data: data,
				});
			}
		},
		onError: (error: any) => {
			haptic('error');
			const errorMessage = error?.response?.data?.message || error?.message || 'Validation failed';
			setScanResult({
				type: 'error',
				errors: [{ error: { Error: errorMessage } }],
			});
		},
		onSettled: () => {
			setIsActive(true);
		},
	});

	const resetScanner = () => {
		setQrData(undefined);
		setScanResult(null);
		setIsActive(true);
		setCameraError(false);
		setUploadError(null);
		cameraFailureReportedRef.current = false;
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	/**
	 * Handles QR code scanning and validation.
	 * Sends the encrypted string directly to the backend for validation,
	 * matching the React Native app approach.
	 */
	const toggleActiveState = async () => {
		if (qrData && isActive) {
			setIsActive(false);

			try {
				sendCode({
					qrData: qrData,
					eventID: Number(eventId),
				});
			} catch (e) {
				airbrake.notify({
					error: e,
					context: { qrData, eventId },
				});
			}
		}
	};

	useEffect(() => {
		if (qrData) {
			toggleActiveState();
		}
	}, [qrData]);

	/**
	 * Decodes a QR code from an uploaded image file using jsQR.
	 * Feeds the decoded payload into the same validation flow as camera scanning.
	 */
	const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setUploadError(null);

		const img = new Image();
		const url = URL.createObjectURL(file);

		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				setUploadError('Canvas not supported in this browser');
				airbrake.notify({
					error: new Error('Canvas not supported in browser'),
					context: {
						fileName: file.name,
						fileType: file.type,
						eventId,
					},
				});
				URL.revokeObjectURL(url);
				return;
			}
			ctx.drawImage(img, 0, 0);
			const imageData = ctx.getImageData(0, 0, img.width, img.height);
			const result = jsQR(imageData.data, imageData.width, imageData.height);
			URL.revokeObjectURL(url);

			if (!result) {
				setUploadError('No QR code found in image');
				return;
			}
			const payload = parseQrPayload(result.data);
			if (payload) {
				setQrData(payload);
			} else {
				setUploadError('QR-code is invalid.');
				airbrake.notify({
					error: new Error('Uploaded QR payload is invalid'),
					context: {
						fileName: file.name,
						fileType: file.type,
						eventId,
					},
				});
			}
		};

		img.onerror = () => {
			setUploadError('Could not load image');
			airbrake.notify({
				error: new Error('Could not load uploaded image for QR fallback decode'),
				context: {
					fileName: file.name,
					fileType: file.type,
					eventId,
				},
			});
			URL.revokeObjectURL(url);
		};

		img.src = url;
	};

	const switchMode = (next: ScannerMode) => {
		if (next === mode) return;
		haptic('tap');
		resetScanner();
		setMode(next);
	};

	return (
		<MainLayout>
			<Styled.ModeSwitch role="tablist" aria-label="Check-in mode">
				{(['camera', 'manual'] as const).map((option) => (
					<Styled.ModeButton
						key={option}
						type="button"
						role="tab"
						aria-selected={mode === option}
						$active={mode === option}
						onClick={() => switchMode(option)}
					>
						{option === 'camera' ? 'Camera' : 'Manual'}
					</Styled.ModeButton>
				))}
			</Styled.ModeSwitch>
			{mode === 'manual' ? (
				<ManualCheckIn eventId={Number(eventId)} />
			) : !scanResult ? (
				<Styled.ReaderContainer>
					{!cameraError ? (
						<CameraScanner
							onScan={(text) => {
								if (!isActive) return;
								const payload = parseQrPayload(text);
								if (payload) {
									setQrData(payload);
								} else {
									setScanResult({
										type: 'error',
										errors: [{ error: { Error: 'Invalid QR code format' } }],
									});
									airbrake.notify({
										error: new Error('Camera QR payload is invalid'),
										context: { eventId },
									});
								}
							}}
							onCameraError={(error) => {
								if (!cameraFailureReportedRef.current) {
									cameraFailureReportedRef.current = true;
									airbrake.notify({
										error: new Error(`Camera initialization failed: ${getErrorMessage(error)}`),
										context: { eventId, errorName: (error as { name?: unknown })?.name },
									});
								}
								setCameraError(true);
							}}
						/>
					) : (
						<div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', width: '90%' }}>
							<p style={{ margin: 0, color: '#666' }}>No camera available. Upload a QR code image to validate:</p>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleImageUpload}
								style={{ fontSize: '14px', cursor: 'pointer' }}
							/>
							{uploadError && <p style={{ margin: 0, color: '#ef4444', fontSize: '13px' }}>{uploadError}</p>}
						</div>
					)}
				</Styled.ReaderContainer>
			) : (
				<ScanResultComponent result={scanResult} onReset={resetScanner} eventId={Number(eventId)} />
			)}
		</MainLayout>
	);
};
